import { useState } from 'react';
import { PAISES_TELEFONE, formatarTelefone, paraE164 } from '../dados/telefone';

/** Só o valor `''` — quem escolhe isto digita o número inteiro, com DDI. */
const OUTRO = '';

/**
 * Telefone com país ao lado.
 *
 * A bandeira não é enfeite: é ela que decide o DDI e o molde da digitação. Sem
 * ela, "11987654321" e "+1 415 555 0132" chegavam no mesmo campo e a única
 * defesa era a pessoa lembrar de escrever o `+`.
 *
 * O que sai daqui é sempre E.164 (`+5511987654321`), num campo escondido. O
 * servidor continua normalizando o que recebe — mas mandar já pronto tira a
 * ambiguidade em vez de terceirizá-la.
 */
export function CampoTelefone({
  name = 'phone',
  paisInicial = 'BR',
  obrigatorio,
  valorInicial,
}: {
  name?: string;
  /** Código ISO-2 do evento; cai no Brasil quando não é um dos cinco. */
  paisInicial?: string | null;
  obrigatorio?: boolean;
  /** Número já gravado, em E.164 (com ou sem `+`) — para editar em vez de digitar do zero. */
  valorInicial?: string | null;
}) {
  const inicial = PAISES_TELEFONE.find((p) => p.codigo === paisInicial) ?? PAISES_TELEFONE[0];

  // O número gravado volta para o molde do país dele, e não para o do evento:
  // um celular português num evento brasileiro continua português.
  const [codigo, setCodigo] = useState(() => (valorInicial ? desmontar(valorInicial).codigo : inicial.codigo));
  const [texto, setTexto] = useState(() => (valorInicial ? desmontar(valorInicial).texto : ''));

  const pais = PAISES_TELEFONE.find((p) => p.codigo === codigo);
  const valorFinal = pais ? paraE164(pais, texto) : texto.trim();

  function trocarPais(novo: string) {
    setCodigo(novo);
    // O que estava digitado pertencia ao molde anterior: um celular brasileiro
    // reaproveitado como número americano vira um número que não existe.
    setTexto('');
  }

  return (
    <label className="campo">
      <span className="campo-label">
        WhatsApp
        {obrigatorio && <span style={{ color: 'var(--danger)' }}> *</span>}
      </span>

      <div className="campo-telefone">
        <select
          className="form-select"
          value={codigo}
          onChange={(e) => trocarPais(e.target.value)}
          aria-label="País do telefone"
        >
          {PAISES_TELEFONE.map((p) => (
            <option key={p.codigo} value={p.codigo}>
              {p.bandeira} +{p.ddi}
            </option>
          ))}
          <option value={OUTRO}>Outro</option>
        </select>

        <input
          className="form-input"
          type="tel"
          required={obrigatorio}
          value={texto}
          placeholder={pais ? pais.exemplo : '+351 912 345 678'}
          onChange={(e) => setTexto(pais ? formatarTelefone(pais, e.target.value) : e.target.value)}
        />
      </div>

      {/* O campo de verdade. O visível é só a forma de digitar. */}
      <input type="hidden" name={name} value={valorFinal} />

      <span className="campo-ajuda">
        {pais
          ? `Formatado enquanto você digita. Vai como +${pais.ddi}… para o WhatsApp.`
          : 'Escreva o número completo, começando com + e o código do país.'}
      </span>
    </label>
  );
}

/** "5547992521144" → Brasil, "(47) 99252-1144". DDI desconhecido vai inteiro, em "Outro". */
function desmontar(e164: string) {
  const digitos = e164.replace(/\D/g, '');
  const pais = PAISES_TELEFONE.find((p) => digitos.startsWith(p.ddi));

  return pais
    ? { codigo: pais.codigo, texto: formatarTelefone(pais, digitos.slice(pais.ddi.length)) }
    : { codigo: OUTRO, texto: `+${digitos}` };
}
